import type { WorkflowDetails } from '../types/workflow';
import {
  calculateDisplayDuration,
  formatAppliedOn,
  formatLeaveDate,
  formatLeaveDuration,
  formatPermissionDate,
  formatPermissionDuration,
  formatPermissionTime,
  formatWfhDate,
} from './dateTime';
import { formatDisplayLabel } from './camelCaseLabel';
import {
  formatLeaveSessionLabel,
  leavePortionLabelForDays,
} from './leaveSession';
import { formatWfhDurationLabel } from './wfhSession';
import { isRejectedWorkflowStatus, isCancelledWorkflowStatus } from './workflowStatus';

export type HrAdminApprovalField = {
  label: string;
  value: string;
  expandable?: boolean;
  link?: boolean;
};

function text(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function numberValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function code(value: string | null | undefined): string {
  return (value || '').toUpperCase();
}

export function isLeaveApprovalType(value: string | null | undefined): boolean {
  const normalized = code(value);
  return (
    normalized === 'LEAVE_REQUEST' ||
    normalized === 'LEAVE' ||
    normalized === 'LEAVE_APPLICATION'
  );
}

export function isWfhApprovalType(value: string | null | undefined): boolean {
  const normalized = code(value);
  return normalized === 'WFH_REQUEST' || normalized === 'WFH';
}

export function isPermissionApprovalType(
  value: string | null | undefined,
): boolean {
  const normalized = code(value);
  return (
    normalized === 'PERMISSION_REQUEST' || normalized === 'PERMISSION'
  );
}

export function formatWorkflowApprover(
  details: WorkflowDetails,
): string | null {
  return text(details.currentStep?.stepName);
}

function leaveFields(
  details: WorkflowDetails,
): HrAdminApprovalField[] {
  const requestDetails = details.requestDetails ?? null;
  const leaveType = formatDisplayLabel(
    text(requestDetails?.leaveTypeName) ||
      text(requestDetails?.leaveName) ||
      details.requestTypeLabel,
    'Not available',
  );
  const fromDate = formatLeaveDate(
    text(
      requestDetails?.leaveFromDate ??
        requestDetails?.fromDate ??
        requestDetails?.startDate,
    ),
  );
  const toDate = formatLeaveDate(
    text(
      requestDetails?.leaveToDate ??
        requestDetails?.toDate ??
        requestDetails?.endDate,
    ),
  );
  const requestedDays = numberValue(
    requestDetails?.totalDays ??
      requestDetails?.leaveDays ??
      requestDetails?.numberOfDays,
  );
  const totalDays = formatLeaveDuration(requestedDays);
  const session = formatLeaveSessionLabel(
    text(requestDetails?.sessionType ?? requestDetails?.session),
    [],
    text(requestDetails?.sessionTypeName ?? requestDetails?.sessionName),
  );
  const reason =
    text(requestDetails?.reason) ||
    text(requestDetails?.leaveReason) ||
    'Not available';
  const handoverNotes =
    text(requestDetails?.handoverNotes) ||
    text(requestDetails?.handover_notes) ||
    'Not available';
  const attachment = text(
    requestDetails?.attachmentName ?? requestDetails?.attachment,
  );
  const fields: HrAdminApprovalField[] = [
    { label: 'Leave Type', value: leaveType },
    { label: 'From Date', value: fromDate },
    { label: 'To Date', value: toDate },
    { label: 'Total Days', value: totalDays },
    { label: leavePortionLabelForDays(requestedDays), value: session },
    { label: 'Reason', value: reason },
    { label: 'Handover Notes', value: handoverNotes, expandable: true },
  ];
  if (attachment) {
    fields.push({ label: 'Attachments', value: attachment, link: true });
  }
  return fields;
}

function wfhFields(details: WorkflowDetails): HrAdminApprovalField[] {
  const requestDetails = details.requestDetails ?? null;
  const fromDate = formatWfhDate(
    text(
      requestDetails?.fromDate ??
        requestDetails?.wfhDate ??
        requestDetails?.workFromDate ??
        requestDetails?.wfhFromDate ??
        requestDetails?.requestDate,
    ),
  );
  const toDate = formatWfhDate(
    text(
      requestDetails?.toDate ??
        requestDetails?.workToDate ??
        requestDetails?.wfhToDate ??
        requestDetails?.fromDate ??
        requestDetails?.wfhDate ??
        requestDetails?.workFromDate,
    ),
  );
  const dateValue = fromDate === toDate ? fromDate : `${fromDate} – ${toDate}`;
  const workFrom =
    text(requestDetails?.workLocation) ||
    text(requestDetails?.workFrom) ||
    'Home';
  const reason =
    text(requestDetails?.reason) ||
    text(requestDetails?.wfhReason) ||
    'Not available';
  const duration = formatWfhDurationLabel({
    durationName: text(requestDetails?.durationName),
    sessionName: text(requestDetails?.sessionName ?? requestDetails?.sessionTypeName),
    isHalfDay: requestDetails?.isHalfDay === true,
  });
  return [
    { label: 'Date', value: dateValue },
    ...(duration ? [{ label: 'Duration', value: duration }] : []),
    { label: 'Work From', value: workFrom },
    { label: 'Reason', value: reason },
  ];
}

function permissionFields(
  details: WorkflowDetails,
): HrAdminApprovalField[] {
  const requestDetails = details.requestDetails ?? null;
  const permissionDate = formatPermissionDate(
    text(requestDetails?.permissionDate ?? requestDetails?.requestDate),
  );
  const fromTime = text(requestDetails?.fromTime ?? requestDetails?.startTime);
  const toTime = text(requestDetails?.toTime ?? requestDetails?.endTime);
  const durationText = formatPermissionDuration(
    numberValue(requestDetails?.durationMinutes) ??
      calculateDisplayDuration(fromTime, toTime),
  );
  const reason = text(requestDetails?.reason) || 'Not available';
  return [
    { label: 'Date', value: permissionDate },
    { label: 'From Time', value: formatPermissionTime(fromTime) },
    { label: 'To Time', value: formatPermissionTime(toTime) },
    { label: 'Duration', value: durationText },
    { label: 'Reason', value: reason },
  ];
}

export function getHrAdminApprovalFields(
  details: WorkflowDetails,
  options?: { includeShared?: boolean },
): HrAdminApprovalField[] {
  const includeShared = options?.includeShared !== false;
  let fields: HrAdminApprovalField[] = [];
  if (isLeaveApprovalType(details.requestTypeCode)) {
    fields = leaveFields(details);
  } else if (isWfhApprovalType(details.requestTypeCode)) {
    fields = wfhFields(details);
  } else if (isPermissionApprovalType(details.requestTypeCode)) {
    fields = permissionFields(details);
  }

  if (includeShared) {
    fields = [
      ...fields,
      {
        label: 'Applied On',
        value: formatAppliedOn(details.submittedAt),
      },
    ];
    if (isRejectedWorkflowStatus(details.workflowStatusCode)) {
      const storedRejectReason =
        details.rejectReason ||
        text(details.requestDetails?.rejectReason) ||
        text(details.requestDetails?.rejectionReason);
      fields.push({
        label: 'Reject Reason',
        value: storedRejectReason || 'Not available',
        expandable: true,
      });
    }
    if (isCancelledWorkflowStatus(details.workflowStatusCode)) {
      const storedCancellationReason =
        details.cancellationReason ||
        text(details.requestDetails?.cancellationReason) ||
        text(details.requestDetails?.cancelReason) ||
        text(details.requestDetails?.cancelledReason);
      fields.push({
        label: 'Cancellation Reason',
        value: storedCancellationReason || 'Not available',
        expandable: true,
      });
    }
  }
  return fields;
}
